import { useEffect, useRef, useState } from "preact/hooks";
import { requestCode, verifyCode } from "../net/api";
import { CODE_LENGTH, loginError, normalizeCode, resendLeft } from "./loginCode";
import { Panel } from "./Panel";

export function LoginPanel() {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step !== "code") return;
    codeInput.current?.focus();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [step]);

  useEffect(() => {
    if (step === "code" && !busy) codeInput.current?.focus(); // after a failed verify, let the user retype at once
  }, [step, busy]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await requestCode(email.trim());
      setDevCode(r.code ?? null);
      setSentAt(Date.now());
      setNow(Date.now());
      setCode("");
      setStep("code");
    } catch (err) {
      setError(loginError(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setBusy(true);
    setError(null);
    try {
      await verifyCode(email.trim(), value);
      location.reload(); // main.ts reads /v1/me and enters the world, as after any fresh load
    } catch (err) {
      setError(loginError(err));
      setCode("");
      setBusy(false);
    }
  }

  function onCodeInput(raw: string) {
    const v = normalizeCode(raw);
    setCode(v);
    if (v.length === CODE_LENGTH && !busy) void verify(v);
  }

  const wait = resendLeft(sentAt, now);
  return (
    <Panel title="登入 Nomen Land">
      {step === "email" ? (
        <form onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <label>Email</label>
          <input type="email" required autocomplete="email" value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
          <button type="submit" disabled={busy}>寄送驗證碼</button>
          {error && <div class="error">{error}</div>}
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (code.length === CODE_LENGTH) void verify(code); }}>
          <p class="hint">若此 email 已受邀，驗證碼會在一分鐘內寄達 {email.trim()}。</p>
          {devCode && <p class="hint">開發模式驗證碼：<code>{devCode}</code></p>}
          <label>驗證碼</label>
          <input ref={codeInput} type="text" inputmode="numeric" autocomplete="one-time-code"
            value={code} disabled={busy} onInput={(e) => onCodeInput((e.target as HTMLInputElement).value)} />
          <button type="submit" disabled={busy || code.length !== CODE_LENGTH}>登入</button>
          <button type="button" class="secondary" disabled={busy || wait > 0} onClick={() => void send()}>
            {wait > 0 ? `重新寄送（${wait}）` : "重新寄送"}
          </button>
          <button type="button" class="secondary" disabled={busy}
            onClick={() => { setStep("email"); setError(null); setDevCode(null); }}>改用其他 email</button>
          {error && <div class="error">{error}</div>}
        </form>
      )}
    </Panel>
  );
}
